import type {
  ActiveJob,
  AgentStatus,
  AgentType,
  CommandAuraState,
  ConfidenceLevel,
  ConversationMessage,
  DecisionRoomState,
  DecisionTopicStatus,
  DomainState,
  ExecutionReadiness,
  JobStage,
  JobStatus,
  MessageSourceType,
  RiskLevel,
  WhatMattersNowItem,
} from "@/lib/aura/types";
import type { OperationalStateContract } from "./operational-state";

type LiveMetric = {
  label: string;
  value: string;
  status?: string;
};

type LiveMessage = {
  id: string;
  source: string;
  content: string;
  timestamp: string;
};

type LiveOperationalState = {
  topBar?: OperationalStateContract["topBar"];
  auraBrief?: {
    primaryAssessment?: string;
    whatMattersNow?: Array<{
      id: string;
      severity: "safe" | "warning" | "blocked" | "waiting";
      text: string;
    }>;
    belief?: string;
    recommendation?: string;
    executionReadiness?: {
      score: number;
      level: "High" | "Medium" | "Low" | "Blocked";
      blockers: number;
      safeActions: number;
      blockedActions: number;
    };
    evidenceConfidence?: {
      level: "High" | "Medium" | "Low";
      score: number;
      reason: string;
      verifiedChecks: number;
      totalChecks: number;
    };
    requiredApproval?: {
      role: string;
      status: "Required" | "Not Required";
      reason: string;
    };
  };
  truthRail?: OperationalStateContract["truthRail"];
  agents?: Array<{
    id: string;
    name: string;
    role: string;
    status: string;
    currentTask?: string;
  }>;
  jobs?: Array<{
    id: string;
    title: string;
    executor: string;
    worktree?: string;
    branch?: string;
    stage: string;
    auditor?: string;
    decisionRoomStatus: string;
    risk: string;
    status: string;
    lastUpdate: string;
    progress?: number;
    quickActions: readonly string[];
  }>;
  commandThreads?: {
    aura?: readonly LiveMessage[];
    founderAgent?: readonly LiveMessage[];
    codex?: readonly LiveMessage[];
    claude?: readonly LiveMessage[];
    core?: readonly LiveMessage[];
    allAgents?: readonly LiveMessage[];
  };
  decisionTopics?: Array<{
    id: string;
    title: string;
    status: string;
    summary: string;
    participants: readonly string[];
    lastActivity: string;
    blockerCount: number;
    approvalRequired: boolean;
    recommendation: string;
    evidenceSummary: readonly string[];
    riskImpact: readonly string[];
    approvalRequirement: {
      role: string;
      status: string;
      condition: string;
    };
    decisionHistory: readonly string[];
    messages: readonly LiveMessage[];
  }>;
};

const INITIAL_DECISION_TOPIC_LIMIT = 24;
const INITIAL_TOPIC_MESSAGE_LIMIT = 48;
const INITIAL_COMMAND_MESSAGE_LIMIT = 48;
const INITIAL_EVIDENCE_ITEM_LIMIT = 8;

const severityMap: Record<
  "safe" | "warning" | "blocked" | "waiting",
  Pick<WhatMattersNowItem, "priority" | "icon" | "source">
> = {
  safe: { priority: "low", icon: "check", source: "safe-action" },
  warning: { priority: "high", icon: "warning", source: "runtime-warning" },
  blocked: { priority: "critical", icon: "alert", source: "active-blocker" },
  waiting: { priority: "medium", icon: "clock", source: "reviewer-state" },
};

export function adaptLiveOperationalState(
  live: LiveOperationalState,
  fallback: OperationalStateContract
): OperationalStateContract {
  const auraBrief = adaptAuraBrief(live, fallback);
  const jobs = adaptJobs(live.jobs, fallback.jobs);
  const agents = adaptAgents(live.agents, fallback.agents);
  const decisionRoom = adaptDecisionRoom(live, fallback.decisionRoom);

  return {
    ...fallback,
    topBar: live.topBar ?? fallback.topBar,
    auraBrief,
    truthRail: live.truthRail ?? fallback.truthRail,
    agents,
    jobs,
    executionState: {
      domainStates: [
        metricToDomainState("ui-ux", "Execution Readiness", live.truthRail?.readiness),
        metricToDomainState("codex-jobs", "Codex Queue", live.truthRail?.codexQueue),
        metricToDomainState("claude-reviews", "Claude Review", live.truthRail?.claudeReview),
        metricToDomainState("runtime", "Runtime", live.truthRail?.runtime),
      ],
      evidenceConfidence: auraBrief.evidenceConfidence,
      executionReadiness: auraBrief.executionReadiness,
    },
    commandThreads: adaptCommandThreads(live, fallback.commandThreads),
    decisionRoom,
    futureSurfaces: adaptFutureSurfaces(live, fallback.futureSurfaces, jobs),
  };
}

function adaptAuraBrief(
  live: LiveOperationalState,
  fallback: OperationalStateContract
): OperationalStateContract["auraBrief"] {
  const brief = live.auraBrief;

  if (!brief) {
    return fallback.auraBrief;
  }

  const readiness = brief.executionReadiness;
  const evidenceConfidence = brief.evidenceConfidence;

  return {
    primaryAssessment: brief.primaryAssessment ?? fallback.auraBrief.primaryAssessment,
    belief: brief.belief ?? fallback.auraBrief.belief,
    recommendation: brief.recommendation ?? fallback.auraBrief.recommendation,
    whatMattersNow:
      brief.whatMattersNow?.map((item) => ({
        id: item.id,
        text: item.text,
        ...severityMap[item.severity],
      })) ?? fallback.auraBrief.whatMattersNow,
    executionReadiness: readiness
      ? {
          score: readiness.score,
          level: readiness.level === "High" ? "ready" : readiness.level === "Blocked" ? "blocked" : "partial",
          blockers: Array.from({ length: readiness.blockers }, (_, index) =>
            index === 0
              ? brief.primaryAssessment ?? "AURA identified an unresolved blocker."
              : `Additional blocker ${index + 1}`
          ),
          safeActions: readiness.safeActions > 0
            ? [brief.recommendation ?? "Continue safe local execution."]
            : [],
          blockedActions: readiness.blockedActions > 0
            ? [brief.requiredApproval?.reason ?? "Production-impacting execution remains blocked."]
            : [],
        }
      : fallback.auraBrief.executionReadiness,
    evidenceConfidence: evidenceConfidence
      ? {
          level: evidenceConfidence.level.toLowerCase() as ConfidenceLevel,
          score: evidenceConfidence.score,
          reason: evidenceConfidence.reason,
          verifiedChecks: evidenceConfidence.verifiedChecks,
          totalChecks: evidenceConfidence.totalChecks,
        }
      : fallback.auraBrief.evidenceConfidence,
    requiredApprovalRole:
      brief.requiredApproval?.status === "Required" ? "operations-lead" : null,
    requiredApprovalDescription:
      brief.requiredApproval?.status === "Required"
        ? brief.requiredApproval.reason
        : null,
  };
}

function adaptAgents(
  liveAgents: LiveOperationalState["agents"],
  fallback: AgentStatus[]
): AgentStatus[] {
  if (!liveAgents?.length) return fallback;

  return liveAgents.map((agent) => ({
    id: toAgentType(agent.id),
    name: agent.name,
    role: agent.role,
    status:
      agent.status === "Busy"
        ? "busy"
        : agent.status === "Blocked"
          ? "error"
          : "online",
    currentTask: agent.currentTask,
  }));
}

function adaptJobs(
  liveJobs: LiveOperationalState["jobs"],
  fallback: ActiveJob[]
): ActiveJob[] {
  if (Array.isArray(liveJobs) && liveJobs.length === 0) {
    return [];
  }

  if (!liveJobs) return fallback;

  return liveJobs.map((job) => ({
    id: job.id,
    title: job.title,
    status: toJobStatus(job.status),
    progress: job.progress,
    executor: toAgentType(job.executor),
    worktree: job.worktree,
    branch: job.branch,
    stage: toJobStage(job.stage),
    auditor: job.auditor ? toAgentType(job.auditor) : undefined,
    decisionRoomStatus: toDecisionStatus(job.decisionRoomStatus),
    riskLevel: toRiskLevel(job.risk),
    lastUpdate: job.lastUpdate,
    domain: "ui-ux",
    quickActions: job.quickActions.map((label, index) => ({
      id: `${job.id}-action-${index}`,
      label,
      icon: label.includes("Worktree")
        ? "folder"
        : label.includes("Decision")
          ? "message-square"
          : label.includes("Review") || label.includes("Evidence")
            ? "file-search"
            : "eye",
      action: label.toLowerCase().replaceAll(" ", "-"),
    })),
  }));
}

function adaptCommandThreads(
  live: LiveOperationalState,
  fallback: CommandAuraState
): CommandAuraState {
  const allAgents =
    live.commandThreads?.allAgents ??
    sortDecisionTopicsForDisplay(live.decisionTopics ?? [])[0]?.messages;

  if (!allAgents?.length) return fallback;

  return {
    selectedAgentId: fallback.selectedAgentId,
    thread: takeLatestMessages(allAgents, INITIAL_COMMAND_MESSAGE_LIMIT).map(toConversationMessage),
    context: {
      currentState: live.auraBrief?.primaryAssessment ?? fallback.context.currentState,
      relatedDecisionTopic: live.decisionTopics?.[0]?.title,
      currentBlocker: live.truthRail?.blockers.value,
      recommendedAction: {
        text: live.auraBrief?.recommendation ?? fallback.context.recommendedAction.text,
        type: live.auraBrief?.requiredApproval?.status === "Required" ? "hold" : "continue",
      },
      memoryNotes:
        live.decisionTopics?.[0]?.evidenceSummary.slice(0, 3) ??
        fallback.context.memoryNotes,
    },
    suggestedCommands: fallback.suggestedCommands,
  };
}

function adaptDecisionRoom(
  live: LiveOperationalState,
  fallback: DecisionRoomState
): DecisionRoomState {
  const topics = sortDecisionTopicsForDisplay(live.decisionTopics ?? []).slice(
    0,
    INITIAL_DECISION_TOPIC_LIMIT
  );
  if (!topics?.length) return fallback;

  const selectedTopic = topics[0];

  return {
    topics: topics.map((topic) => ({
      id: topic.id,
      title: topic.title,
      summary: topic.summary,
      status: toDecisionStatus(topic.status) ?? "live",
      participants: topic.participants.slice(0, 8).map((participant, index) => ({
        id: `${topic.id}-participant-${index}`,
        name: participant,
        type: toAgentType(participant),
      })),
      blockerCount: topic.blockerCount,
      approvalCount: topic.approvalRequired ? 1 : 0,
      createdAt: topic.lastActivity,
      updatedAt: topic.lastActivity,
    })),
    selectedTopicId: selectedTopic.id,
    thread: takeLatestMessages(selectedTopic.messages, INITIAL_TOPIC_MESSAGE_LIMIT).map(
      toConversationMessage
    ),
    intelligence: {
      evidenceSummary: selectedTopic.evidenceSummary
        .slice(0, INITIAL_EVIDENCE_ITEM_LIMIT)
        .join(" "),
      evidenceItems: selectedTopic.evidenceSummary
        .slice(0, INITIAL_EVIDENCE_ITEM_LIMIT)
        .map((summary, index) => ({
        id: `${selectedTopic.id}-evidence-${index}`,
        title: `Evidence ${index + 1}`,
        source: "Core",
        confidence: "high",
        summary,
        timestamp: selectedTopic.lastActivity,
      })),
      risk: {
        level: selectedTopic.blockerCount > 0 ? "high" : "medium",
        factors: selectedTopic.riskImpact.length
          ? [...selectedTopic.riskImpact]
          : ["No active blocker is recorded for this topic."],
        mitigations: [selectedTopic.recommendation],
      },
      impact: {
        scope: "system",
        affectedSystems: ["CLIMB ONE Admin Console", "Founder Agent", "AURA"],
        estimatedDuration: "Current operational window",
      },
      approvalRequirement: {
        role: selectedTopic.approvalRequirement.role === "None" ? "any" : "operations-lead",
        reason: selectedTopic.approvalRequirement.condition,
      },
      auraRecommendation: {
        action: selectedTopic.recommendation,
        reasoning: selectedTopic.summary,
        confidence: "high",
      },
      history: selectedTopic.decisionHistory.slice(0, 12).map((entry, index) => ({
        id: `${selectedTopic.id}-history-${index}`,
        action: entry,
        actor: "Core",
        actorType: "core",
        timestamp: selectedTopic.lastActivity,
      })),
    },
  };
}

function sortDecisionTopicsForDisplay(
  topics: readonly NonNullable<LiveOperationalState["decisionTopics"]>[number][]
): NonNullable<LiveOperationalState["decisionTopics"]> {
  return [...topics].sort((left, right) => {
    const statusDelta =
      decisionTopicPriority(right.status) - decisionTopicPriority(left.status);
    if (statusDelta !== 0) return statusDelta;

    return toTimestamp(right.lastActivity) - toTimestamp(left.lastActivity);
  });
}

function decisionTopicPriority(status: string): number {
  const normalized = status.toLowerCase();
  if (normalized.includes("live") || normalized.includes("active")) return 4;
  if (normalized.includes("blocked")) return 3;
  if (normalized.includes("waiting")) return 2;
  if (normalized.includes("resolved")) return 1;
  return 0;
}

function takeLatestMessages(
  messages: readonly LiveMessage[],
  limit: number
): readonly LiveMessage[] {
  return messages.length > limit ? messages.slice(-limit) : messages;
}

function toTimestamp(value: string): number {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function adaptFutureSurfaces(
  live: LiveOperationalState,
  fallback: OperationalStateContract["futureSurfaces"],
  jobs: ActiveJob[]
): OperationalStateContract["futureSurfaces"] {
  return fallback.map((surface) => {
    if (surface.id === "codex-queue") {
      return {
        ...surface,
        primaryMetric: `${jobs.length} jobs tracked`,
        evidenceItems: jobs.map((job) => `${job.title} · ${job.status}`),
      };
    }

    if (surface.id === "review-evidence") {
      return {
        ...surface,
        primaryMetric: live.auraBrief?.evidenceConfidence
          ? `${live.auraBrief.evidenceConfidence.level} confidence · ${live.auraBrief.evidenceConfidence.verifiedChecks}/${live.auraBrief.evidenceConfidence.totalChecks}`
          : surface.primaryMetric,
        evidenceItems: live.decisionTopics?.[0]?.evidenceSummary.length
          ? [...live.decisionTopics[0].evidenceSummary]
          : surface.evidenceItems,
      };
    }

    return surface;
  });
}

function metricToDomainState(
  domain: "ui-ux" | "codex-jobs" | "claude-reviews" | "runtime",
  fallbackLabel: string,
  metric?: LiveMetric
): DomainState {
  const value = metric?.value ?? "Unknown";

  return {
    domain,
    label: metric?.label ?? fallbackLabel,
    status: toDomainStatus(value),
    statusLabel: value,
  };
}

function toConversationMessage(message: LiveMessage): ConversationMessage {
  const source = toMessageSource(message.source);

  return {
    id: message.id,
    source,
    agentName: source === "human" ? undefined : message.source,
    content: message.content,
    timestamp: message.timestamp,
  };
}

function toAgentType(value: string): AgentType {
  const normalized = value.toLowerCase();
  if (normalized.includes("founder")) return "founder-agent";
  if (normalized.includes("codex")) return "codex";
  if (normalized.includes("claude") || normalized.includes("auditor")) return "claude";
  if (normalized.includes("core")) return "core";
  return "aura";
}

function toMessageSource(value: string): MessageSourceType {
  const normalized = value.toLowerCase();
  if (normalized.includes("founder")) return "founder-agent";
  if (normalized.includes("codex")) return "codex";
  if (normalized.includes("claude")) return "claude";
  if (normalized.includes("core")) return "core";
  if (normalized.includes("human")) return "human";
  return "aura";
}

function toJobStatus(value: string): JobStatus {
  const normalized = value.toLowerCase();
  if (normalized.includes("running")) return "running";
  if (normalized.includes("completed")) return "completed";
  if (normalized.includes("failed") || normalized.includes("blocked")) return "failed";
  if (normalized.includes("paused")) return "paused";
  if (normalized.includes("queued")) return "queued";
  return "pending";
}

function toJobStage(value: string): JobStage {
  const normalized = value.toLowerCase();
  if (normalized.includes("implementation") || normalized.includes("running")) return "implementation";
  if (normalized.includes("test")) return "testing";
  if (normalized.includes("review")) return "review";
  if (normalized.includes("deploy")) return "deployment";
  if (normalized.includes("verified")) return "verification";
  if (normalized.includes("complete")) return "complete";
  return "planning";
}

function toDecisionStatus(value: string): DecisionTopicStatus | null {
  const normalized = value.toLowerCase();
  if (normalized.includes("blocked")) return "blocked";
  if (normalized.includes("waiting")) return "waiting-review";
  if (normalized.includes("resolved")) return "resolved";
  if (normalized.includes("active") || normalized.includes("live")) return "live";
  return null;
}

function toRiskLevel(value: string): RiskLevel {
  const normalized = value.toLowerCase();
  if (normalized.includes("critical")) return "critical";
  if (normalized.includes("high") || normalized.includes("blocked")) return "high";
  if (normalized.includes("low") || normalized.includes("normal")) return "low";
  return "medium";
}

function toDomainStatus(value: string): DomainState["status"] {
  const normalized = value.toLowerCase();
  if (normalized.includes("ready") || normalized.includes("stable") || normalized.includes("live")) return "stable";
  if (normalized.includes("blocked")) return "blocked";
  if (normalized.includes("waiting")) return "waiting";
  if (normalized.includes("paused")) return "paused";
  if (normalized.includes("active")) return "active";
  if (normalized.includes("incomplete") || normalized.includes("medium")) return "incomplete";
  return "unknown";
}
